import { useMemoizedFn } from "ahooks";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface UseKeyboardNavigationOptions {
  /** 目标元素 */
  target?:
    | HTMLElement
    | null
    | (() => HTMLElement | null)
    | MutableRefObject<HTMLElement | null>;
  /** 列表项总数 */
  itemCount: number;
  /** 是否启用键盘导航，默认为 true */
  enabled?: boolean;
  /** 是否循环切换，默认为 false */
  circular?: boolean;
  /** 初始活动项索引，默认为 -1（无活动项） */
  initialIndex?: number;
  /** 活动项变化时的回调函数 */
  onActiveChange?: (index: number) => void;
  /** 选择项时的回调函数（Enter键或点击） */
  onSelect?: (index: number) => void;
  /** 自定义键盘事件处理器 */
  onKeyDown?: (event: KeyboardEvent) => void;
}

export interface UseKeyboardNavigationReturn {
  /** 当前活动项的索引 */
  activeIndex: number;
  /** 设置活动项索引 */
  setActiveIndex: (index: number) => void;
  /** 向上切换活动项 */
  moveUp: () => void;
  /** 向下切换活动项 */
  moveDown: () => void;
  /** 移动到第一项 */
  moveToFirst: () => void;
  /** 移动到最后一项 */
  moveToLast: () => void;
  /** 清除活动项 */
  clearActive: () => void;
  /** 检查指定索引是否为活动项 */
  isActive: (index: number) => boolean;
  /** 键盘事件处理器（用于手动绑定） */
  handleKeyDown: (event: KeyboardEvent) => void;
}

function getTargetElement(
  target:
    | HTMLElement
    | null
    | (() => HTMLElement | null)
    | MutableRefObject<HTMLElement | null>,
  defaultTarget: Window | Document = window
) {
  if (!target) {
    return defaultTarget;
  }
  if (typeof target === "function") {
    return target();
  }
  return (
    (target as MutableRefObject<HTMLElement | null>).current || defaultTarget
  );
}

/**
 * 用于方向键控制上下切换当前活动列表的React Hook
 *
 * @param options 配置选项
 * @returns 导航控制方法和状态
 *
 * @example
 * ```tsx
 * const { activeIndex, moveUp, moveDown, isActive } = useKeyboardNavigation({
 *   itemCount: items.length,
 *   onSelect: (index) => console.log('Selected:', items[index])
 * });
 *
 * return (
 *   <div>
 *     {items.map((item, index) => (
 *       <div
 *         key={index}
 *         className={isActive(index) ? 'active' : ''}
 *       >
 *         {item.name}
 *       </div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useKeyboardNavigation(
  options: UseKeyboardNavigationOptions
): UseKeyboardNavigationReturn {
  const {
    itemCount,
    enabled = true,
    circular = false,
    initialIndex = -1,
    onActiveChange,
    onSelect,
    onKeyDown,
    target,
  } = options;

  const [activeIndex, setActiveIndexState] = useState(initialIndex);

  // 设置活动项索引
  const setActiveIndex = useCallback(
    (index: number) => {
      if (index >= -1 && index < itemCount) {
        setActiveIndexState(index);
        onActiveChange?.(index);
      }
    },
    [itemCount, onActiveChange]
  );

  // 向上切换
  const moveUp = useCallback(() => {
    if (itemCount === 0) return;

    let newIndex: number;
    if (activeIndex <= 0) {
      newIndex = circular ? itemCount - 1 : 0;
    } else {
      newIndex = activeIndex - 1;
    }

    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, itemCount, circular, setActiveIndex]);

  // 向下切换
  const moveDown = useCallback(() => {
    if (itemCount === 0) return;

    let newIndex: number;
    if (activeIndex >= itemCount - 1) {
      newIndex = circular ? 0 : itemCount - 1;
    } else {
      newIndex = activeIndex + 1;
    }

    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, itemCount, circular, setActiveIndex]);

  // 移动到第一项
  const moveToFirst = useCallback(() => {
    if (itemCount > 0) {
      setActiveIndex(0);
    }
  }, [itemCount, setActiveIndex]);

  // 移动到最后一项
  const moveToLast = useCallback(() => {
    if (itemCount > 0) {
      setActiveIndex(itemCount - 1);
    }
  }, [itemCount, setActiveIndex]);

  // 清除活动项
  const clearActive = useCallback(() => {
    setActiveIndex(-1);
  }, [setActiveIndex]);

  // 检查指定索引是否为活动项
  const isActive = useCallback(
    (index: number) => {
      return activeIndex === index;
    },
    [activeIndex]
  );

  // 键盘事件处理器
  const handleKeyDown = useMemoizedFn((event: KeyboardEvent) => {
    if (!enabled) return;

    // 调用自定义键盘事件处理器
    onKeyDown?.(event);

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        moveUp();
        break;
      case "ArrowDown":
        event.preventDefault();
        moveDown();
        break;
      case "Home":
        event.preventDefault();
        moveToFirst();
        break;
      case "End":
        event.preventDefault();
        moveToLast();
        break;
      case "Enter":
      case " ":
        if (activeIndex >= 0 && activeIndex < itemCount) {
          event.preventDefault();
          onSelect?.(activeIndex);
        }
        break;
      case "Escape":
        event.preventDefault();
        clearActive();
        break;
    }
  });

  // 绑定键盘事件监听器
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDownEvent = (event: KeyboardEvent) => {
      handleKeyDown(event);
    };

    // 如果指定了容器元素，则只在该容器内监听
    const targetEl = getTargetElement(target, window);
    targetEl.addEventListener("keydown", handleKeyDownEvent);

    return () => {
      targetEl.removeEventListener("keydown", handleKeyDownEvent);
    };
  }, [enabled, handleKeyDown]);

  // 当itemCount变化时，调整activeIndex
  useEffect(() => {
    if (activeIndex >= itemCount) {
      setActiveIndex(itemCount - 1);
    }
  }, [itemCount, activeIndex, setActiveIndex]);

  return {
    activeIndex,
    setActiveIndex,
    moveUp,
    moveDown,
    moveToFirst,
    moveToLast,
    clearActive,
    isActive,
    handleKeyDown,
  };
}

/**
 * 用于设置键盘导航容器的ref hook
 * 当需要在特定容器内监听键盘事件时使用
 */
export function useKeyboardNavigationContainer() {
  const containerRef = useRef<HTMLElement | null>(null);

  const setContainerRef = useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  return { containerRef, setContainerRef };
}
